import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { loadAnalytics } from '@/shared/lib/analytics'
import { getCookie, setCookie } from '@/shared/lib/cookies'
import { Button } from '@/shared/ui'

const CONSENT_COOKIE = 'humania_cookie_consent'

/**
 * Banner mínimo de consentimiento. GA4 solo se inyecta si el visitante
 * acepta aquí — el env var (`VITE_GA_MEASUREMENT_ID`) por sí solo no basta,
 * así queda respetado el consentimiento por construcción y no solo por
 * configuración.
 *
 * Se monta en `App.jsx` como hermano de `<RouterProvider>` (fuera del
 * contexto de Router) para poder mostrarse sobre cualquier ruta sin
 * duplicarse en los tres árboles de layout separados que arma `router.jsx`.
 * Por eso el enlace legal es un `<a>` plano y no un `<Link>`: `Link` requiere
 * contexto de Router y aquí no lo hay.
 */
export default function CookieConsentBanner() {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const consent = getCookie(CONSENT_COOKIE)
    if (consent === 'accepted') {
      loadAnalytics()
    } else if (!consent) {
      setVisible(true)
    }
  }, [])

  const accept = () => {
    setCookie(CONSENT_COOKIE, 'accepted')
    loadAnalytics()
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="region"
      aria-label={t('cookies.title')}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-ink-100 bg-(--humania-surface) px-4 py-4 shadow-[0_-6px_20px_rgba(15,23,42,.12)] backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-600">
          {t('cookies.message')}{' '}
          <a href="/legal/privacidad" className="font-medium text-brand-600 underline">
            {t('cookies.link')}
          </a>
        </p>
        <Button type="button" onClick={accept} className="shrink-0">
          {t('cookies.accept')}
        </Button>
      </div>
    </div>
  )
}
